import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { Question } from '@/shared/workflowAITypes';

interface QuestionFormProps {
  questions: Question[];
  onSubmit: (answers: Record<string, string | string[] | boolean>) => void;
  disabled?: boolean;
}

export function QuestionForm({ questions, onSubmit, disabled }: QuestionFormProps) {
  const [answers, setAnswers] = useState<Record<string, string | string[] | boolean>>({});

  const setAnswer = (qId: string, value: string | string[] | boolean) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const toggleMulti = (qId: string, option: string) => {
    setAnswers(prev => {
      const current = (prev[qId] as string[]) || [];
      const next = current.includes(option)
        ? current.filter(o => o !== option)
        : [...current, option];
      return { ...prev, [qId]: next };
    });
  };

  const allAnswered = questions.every(q => {
    const a = answers[q.id];
    if (a === undefined || a === '') return false;
    if (Array.isArray(a) && a.length === 0) return false;
    return true;
  });

  const handleSubmit = () => {
    if (!allAnswered) return;
    onSubmit(answers);
  };

  return (
    <div className="space-y-4">
      {questions.map((q, qIdx) => (
        <motion.div
          key={q.id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: qIdx * 0.08 }}
          className="space-y-2"
        >
          <label className="text-sm font-medium text-slate-700 block">
            {q.text}
          </label>

          {/* ── Single select ── */}
          {q.type === 'single_select' && q.options && (
            <div className="flex flex-wrap gap-2">
              {q.options.map(opt => {
                const isSelected = answers[q.id] === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => setAnswer(q.id, opt)}
                    disabled={disabled}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-200',
                      isSelected
                        ? 'border-workfinity-400 bg-workfinity-50 text-workfinity-700 shadow-sm ring-1 ring-workfinity-200'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
                      disabled && 'opacity-60 pointer-events-none',
                    )}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Multi select ── */}
          {q.type === 'multi_select' && q.options && (
            <div className="flex flex-wrap gap-2">
              {q.options.map(opt => {
                const selected = ((answers[q.id] as string[]) || []).includes(opt);
                return (
                  <button
                    key={opt}
                    onClick={() => toggleMulti(q.id, opt)}
                    disabled={disabled}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-200',
                      selected
                        ? 'border-workfinity-400 bg-workfinity-50 text-workfinity-700 shadow-sm ring-1 ring-workfinity-200'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
                      disabled && 'opacity-60 pointer-events-none',
                    )}
                  >
                    {selected ? '✓ ' : ''}{opt}
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Boolean ── */}
          {q.type === 'boolean' && (
            <div className="flex gap-2">
              {['Yes', 'No'].map(opt => {
                const boolVal = opt === 'Yes';
                const isSelected = answers[q.id] === boolVal;
                return (
                  <button
                    key={opt}
                    onClick={() => setAnswer(q.id, boolVal)}
                    disabled={disabled}
                    className={cn(
                      'px-4 py-1.5 rounded-lg text-xs font-medium border transition-all duration-200',
                      isSelected
                        ? 'border-workfinity-400 bg-workfinity-50 text-workfinity-700 shadow-sm ring-1 ring-workfinity-200'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
                      disabled && 'opacity-60 pointer-events-none',
                    )}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Text ── */}
          {q.type === 'text' && (
            <input
              type="text"
              value={(answers[q.id] as string) || ''}
              onChange={e => setAnswer(q.id, e.target.value)}
              disabled={disabled}
              placeholder="Type your answer..."
              className={cn(
                'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700',
                'placeholder:text-slate-400 focus:border-workfinity-300 focus:ring-1 focus:ring-workfinity-200 outline-none transition-all',
                disabled && 'opacity-60 pointer-events-none',
              )}
            />
          )}
        </motion.div>
      ))}

      <div className="pt-2 flex justify-end">
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!allAnswered || disabled}
          className="bg-gradient-to-r from-workfinity-500 to-workfinity-600 hover:from-workfinity-600 hover:to-workfinity-700 text-white shadow-sm"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          Submit Answers
        </Button>
      </div>
    </div>
  );
}
